import { Injectable } from '@angular/core';

import { RoomTypes } from '@app/origami-api/constants/room-types';
import { Topic, TopicUtils } from '@app/origami-api/utils/topic-utils';
import { StringHash } from '@app/shared/utils/utils-types';
import { FieldNames } from '@app/origami-api/constants/field-names';
import { SiteServiceModel } from '@app/origami-api/services/site.service';
import { RoleLevel } from '@app/origami-api/constants/role-levels';

export interface RoomTypeField {
  name: string;
  icon: string;
  title: string;
  shortTitle: string;
}

export interface ServiceOffering {
  id: number;
  LongName: string;
  ShortName: string;
}

@Injectable({
  providedIn: 'root',
})
export class KpiTabDataService {
  public room: SiteServiceModel.Site;

  private readonly kpiIconsByFieldName = {
    [FieldNames.Site.TotalDocumentCount]: 'file',
    [FieldNames.Site.CountriesCount]: 'earth-americas',
    [FieldNames.Site.PendingSitesCount]: 'sitemap',
    [FieldNames.Site.ActiveSitesCount]: 'hospital far',
    [FieldNames.Site.AddDocuments]: 'file far',
    [FieldNames.Site.AddUser]: 'plus-circle',
    [FieldNames.Site.AddTask]: 'tasks',
    [FieldNames.Site.AddSite]: 'flask',
  };

  private readonly kpiTitlesByFieldName = {
    [FieldNames.Site.TotalDocumentCount]: tiStrings.enum.additionalKPIsNames.totalDocuments,
    [FieldNames.Site.CountriesCount]: tiStrings.enum.additionalKPIsNames.countries,
    [FieldNames.Site.PendingSitesCount]: tiStrings.enum.additionalKPIsNames.pendingSites,
    [FieldNames.Site.ActiveSitesCount]: tiStrings.enum.additionalKPIsNames.activeSites,
    [FieldNames.Site.CollectedDocCount]: tiStrings.enum.KPIsNames.collectedDocuments,
    [FieldNames.Site.MissingDocCount]: tiStrings.enum.KPIsNames.missingDocuments,
    [FieldNames.Site.RequireCodingDocCount]: tiStrings.enum.KPIsNames.requiredCoding,
    [FieldNames.Site.FinalDocumentCount]: tiStrings.enum.KPIsNames.finalDocuments,
    [FieldNames.Site.ExpiredDocCount]: tiStrings.enum.KPIsNames.expiredDocuments,
    [FieldNames.Site.ExpiringDocCount]: tiStrings.enum.KPIsNames.expiringDocuments,
    [FieldNames.Site.DocumentQueryCount]: tiStrings.enum.KPIsNames.openQueries,
    [FieldNames.Site.WaitingESignatureDocCount]: tiStrings.enum.KPIsNames.waitingESignatureDocCount,
    [FieldNames.Site.ESignatureInProgressDocCount]: tiStrings.enum.KPIsNames.eSignatureInProgressDocCount,
    [FieldNames.Site.PeriodicReviewDocCount]: tiStrings.enum.KPIsNames.periodicReviewDocCount,
    [FieldNames.Site.RejectedDocumentsCount]: tiStrings.enum.KPIsNames.rejectedDocuments,
  };

  private readonly kpiShortTitlesByFieldName = {
    [FieldNames.Site.CollectedDocCount]: tiStrings.enum.KPIsShortNames.collected,
    [FieldNames.Site.MissingDocCount]: tiStrings.enum.KPIsShortNames.missing,
    [FieldNames.Site.RequireCodingDocCount]: tiStrings.enum.KPIsShortNames.required,
    [FieldNames.Site.FinalDocumentCount]: tiStrings.enum.KPIsShortNames.final,
    [FieldNames.Site.ExpiredDocCount]: tiStrings.enum.KPIsShortNames.expired,
    [FieldNames.Site.ExpiringDocCount]: tiStrings.enum.KPIsShortNames.expiring,
    [FieldNames.Site.DocumentQueryCount]: tiStrings.enum.KPIsShortNames.queries,
    [FieldNames.Site.RejectedDocumentsCount]: tiStrings.enum.KPIsShortNames.rejected,
  };

  constructor() {}

  public getKpiIconsByFieldName(fieldName) {
    return this.kpiIconsByFieldName[fieldName];
  }

  public getKpiTitlesByFieldName(fieldName) {
    return this.kpiTitlesByFieldName[fieldName];
  }

  public getKpiShortTitlesByFieldName(fieldName) {
    return this.kpiShortTitlesByFieldName[fieldName];
  }

  public isTabAvailable(fieldName: string, room?: SiteServiceModel.Site) {
    room = room || this.room;

    if (!room) {
      return false;
    }

    const isEnabledModuleWorkflowField = room.SiteFields.find(
      (siteField) => siteField.FieldName === FieldNames.Site.GeneralModuleEnableWorkflow,
    );
    const isEnabledModuleAuditField = room.SiteFields.find(
      (siteField) => siteField.FieldName === FieldNames.Site.GeneralModuleEnableAudit,
    );
    const isEnabledModuleQueryResolution = room.SiteFields.find(
      (siteField) => siteField.FieldName === FieldNames.Site.GeneralModuleEnableQueryResolution,
    );
    const useEsignature = room.SiteFields.find(
      (siteField) => siteField.FieldName === FieldNames.Site.SecurityEsignatureUseEsignature,
    );

    const isReader = room.RoleLevel === RoleLevel.READER;
    const isEditor = room.RoleLevel === RoleLevel.EDITOR;
    const isAdminOrHigher = room.RoleLevel >= RoleLevel.ADMIN;

    let isAvailable = true;

    switch (fieldName) {
      case FieldNames.Site.RequireCodingDocCount:
        if (isEnabledModuleWorkflowField && isEnabledModuleWorkflowField.Fieldvalue !== '1') {
          return null;
        } else {
          isAvailable = !isReader;
        }
        break;
      case FieldNames.Site.DocumentQueryCount:
        const isDocumentQueryEnabled =
          isEnabledModuleQueryResolution && isEnabledModuleQueryResolution.Fieldvalue === '1';
        if (!isDocumentQueryEnabled) {
          return null;
        }
        break;
      case FieldNames.Site.WaitingESignatureDocCount:
        if (useEsignature && useEsignature.Fieldvalue === '0') {
          return null;
        }
        break;
      case FieldNames.Site.ESignatureInProgressDocCount:
        if (useEsignature && useEsignature.Fieldvalue === '0') {
          return null;
        }
        break;
      case FieldNames.Site.PeriodicReviewDocCount:
        isAvailable = isEditor || isAdminOrHigher; // (isEditor && (room.IsUserDocumentManager || room.IsUserDocumentOwner))
        break;
      case FieldNames.Site.FinalDocumentCount:
      case FieldNames.Site.RejectedDocumentsCount:
        if (isEnabledModuleWorkflowField && isEnabledModuleWorkflowField.Fieldvalue !== '1') {
          return null;
        }
        break;
    }

    return isAvailable;
  }
}

export class RoomType {
  selectedSorter: string;
  readonly system: boolean;

  get allFields(): string[] {
    return [...this.sortings, ...this.kpis, ...this.additionalKpis].reduce<string[]>((result, item) => {
      if (result.indexOf(item) === -1) {
        result.push(item);
      }
      return result;
    }, []);
  }

  static create(topicOrId: RoomTypes | Topic, kpiTabDataService: KpiTabDataService, fieldsTitles?: StringHash<string>) {
    if (typeof topicOrId === 'number') {
      return new RoomType(
        topicOrId,
        tiStrings.enum.roomFilterNames[topicOrId],
        0,
        [],
        [],
        [],
        [],
        null,
        kpiTabDataService,
      );
    }

    const id = TopicUtils.getTopicFieldNumberValue(topicOrId, FieldNames.RoomType.RoomTypeId);
    const count = TopicUtils.getTopicFieldNumberValue(topicOrId, FieldNames.RoomType.Count) || 0;
    const sortings = TopicUtils.getTopicFieldValue(topicOrId, FieldNames.RoomType.Sortings);
    const kpis = TopicUtils.getTopicFieldValue(topicOrId, FieldNames.RoomType.KPIs);
    const additionalKpis = TopicUtils.getTopicFieldValue(topicOrId, FieldNames.RoomType.AdditionalKPIs);
    const addActions = TopicUtils.getTopicFieldValue(topicOrId, FieldNames.RoomType.Actions);

    return new RoomType(
      id,
      tiStrings.enum.roomFilterNames[id] || topicOrId.TopicName,
      count,
      sortings ? sortings.split(',') : [],
      kpis ? kpis.split(',') : [],
      additionalKpis ? additionalKpis.split(',') : [],
      addActions ? addActions.split(',') : [],
      fieldsTitles,
      kpiTabDataService,
    );
  }

  private constructor(
    public readonly id: RoomTypes,
    public readonly text: string,
    public count: number,
    public readonly sortings: string[],
    public readonly kpis: string[],
    public readonly additionalKpis: string[],
    public readonly addActions: string[],
    readonly fieldsTitles: StringHash<string>,
    readonly kpiTabDataService: KpiTabDataService,
  ) {
    this.system = [RoomTypes.All, RoomTypes.Favorite, RoomTypes.Recent].indexOf(id) !== -1;
    this.fieldsTitles = this.fieldsTitles || {};
  }

  getSortings(): RoomTypeField[] {
    return this.convertFields(this.sortings);
  }

  getKpis(): RoomTypeField[] {
    return this.convertFields(this.kpis);
  }

  getAdditionalKpis(): RoomTypeField[] {
    return this.convertFields(this.additionalKpis);
  }

  getAddActions() {
    return this.addActions;
  }

  private convertFields(items: string[]): RoomTypeField[] {
    return items.map((item) => ({
      name: item,
      icon: this.kpiTabDataService.getKpiIconsByFieldName(item),
      title: this.kpiTabDataService.getKpiTitlesByFieldName(item) || this.fieldsTitles[item],
      shortTitle: this.kpiTabDataService.getKpiShortTitlesByFieldName(item) || this.fieldsTitles[item],
    }));
  }
}
